import { isEqual } from 'lodash';
import { DateTime } from 'luxon';

import { InputErpAnhangAttachementsRelation } from '@work4all/models/lib/Classes/InputErpAnhangAttachementsRelation.entity';
import { InputTravelExpensesReciptRelation } from '@work4all/models/lib/Classes/InputTravelExpensesReciptRelation.entity';
import { InputTravelReciptRelation } from '@work4all/models/lib/Classes/InputTravelReciptRelation.entity';
import { TravelReceipts } from '@work4all/models/lib/Classes/TravelReceipts.entity';

/**
 * Transforms a TravelReceipts entity to InputTravelReciptRelation format
 * based on the GraphQL schema types.
 *
 * @param receipt - TravelReceipts entity to transform
 * @returns InputTravelReciptRelation object with proper field mapping
 */
const transformToInputRelation = (
  receipt: TravelReceipts & { attachements: InputErpAnhangAttachementsRelation }
): InputTravelReciptRelation => {
  return {
    code: typeof receipt.id === 'number' ? receipt.id : undefined,
    projectCode: receipt.projectId ?? undefined,
    typeOfRecipt: receipt.receiptKindId ?? undefined,
    typeOfPayment: receipt.paymentKind ?? undefined,
    costCenterCode: receipt.costCenterId ?? undefined,
    name: receipt.description ?? undefined,
    currencyCode: receipt.currencyId ?? undefined,
    date: receipt.date
      ? DateTime.fromISO(receipt.date).toFormat('yyyy-MM-dd')
      : undefined,
    amount1: receipt.amountGross1 ?? undefined,
    amount2: receipt.amountGross2 ?? undefined,
    amount3: receipt.amountGross3 ?? undefined,
    vat1: receipt.vat1 ?? undefined,
    vat2: receipt.vat2 ?? undefined,
    vat3: receipt.vat3 ?? undefined,
    taxKey1: receipt.taxKey1Value ?? undefined,
    taxKey2: receipt.taxKey2Value ?? undefined,
    taxKey3: receipt.taxKey3Value ?? undefined,
    ledgerAccountCode1: receipt.ledgerAccount1Id ?? undefined,
    ledgerAccountCode2: receipt.ledgerAccount2Id ?? undefined,
    ledgerAccountCode3: receipt.ledgerAccount3Id ?? undefined,
    attachements: receipt.attachements?.add ?? undefined,
    attachementsToRemove:
      receipt.attachements?.remove?.filter((x) => typeof x === 'number') ??
      undefined,
    receiptKindTravelCostCode: receipt.receiptKindTravelCostId ?? undefined,
    singlePrice: receipt.ePrice ?? undefined,
    amount: receipt.amount ?? undefined,
  };
};

/**
 * Calculates the InputTravelExpensesReciptRelation by comparing original receipts
 * with edited receipts to determine what needs to be added, modified, or removed.
 *
 * @param originalReceipts - The original receipts array
 * @param editedReceipts - The edited receipts array (final version)
 * @returns InputTravelExpensesReciptRelation with add, modify, and remove arrays
 */
export const calculateReceiptsRelation = (
  originalReceipts: TravelReceipts[],
  editedReceipts: TravelReceipts[]
): InputTravelExpensesReciptRelation => {
  const receiptsRelation = new InputTravelExpensesReciptRelation();

  // Initialize arrays
  receiptsRelation.add = [];
  receiptsRelation.modify = [];
  receiptsRelation.remove = [];

  // Create maps for efficient lookup
  const originalMap = new Map(
    originalReceipts
      .filter((receipt) => typeof receipt.id === 'number')
      .map((receipt) => [receipt.id, receipt])
  );
  const editedMap = new Map(
    editedReceipts
      .filter((receipt) => typeof receipt.id === 'number')
      .map((receipt) => [receipt.id, receipt])
  );

  // Find items to ADD (new items in edited that aren't in original, or items without numeric ID)
  editedReceipts.forEach((editedReceipt) => {
    if (
      typeof editedReceipt.id !== 'number' ||
      (editedReceipt.id && !originalMap.has(editedReceipt.id))
    ) {
      // New receipt or receipt without numeric ID - transform to InputTravelReciptRelation
      if (receiptsRelation.add) {
        receiptsRelation.add.push(
          transformToInputRelation(
            editedReceipt as TravelReceipts & {
              attachements: InputErpAnhangAttachementsRelation;
            }
          )
        );
      }
    }
  });

  // Find items to MODIFY (items that exist in both but have changes)
  editedReceipts.forEach((editedReceipt) => {
    if (
      typeof editedReceipt.id === 'number' &&
      originalMap.has(editedReceipt.id)
    ) {
      const originalReceipt = originalMap.get(editedReceipt.id);

      // Transform both to InputTravelReciptRelation format for comparison
      const editedTransformed = transformToInputRelation(
        editedReceipt as TravelReceipts & {
          attachements: InputErpAnhangAttachementsRelation;
        }
      );
      const originalTransformed = originalReceipt
        ? transformToInputRelation(
            originalReceipt as TravelReceipts & {
              attachements: InputErpAnhangAttachementsRelation;
            }
          )
        : null;

      // Compare the transformed objects
      if (
        originalTransformed &&
        !isEqual(editedTransformed, originalTransformed)
      ) {
        if (receiptsRelation.modify) {
          receiptsRelation.modify.push(editedTransformed);
        }
      }
    }
  });

  // Find items to REMOVE (items in original that aren't in edited, and have numeric IDs)
  originalReceipts.forEach((originalReceipt) => {
    if (
      typeof originalReceipt.id === 'number' &&
      !editedMap.has(originalReceipt.id)
    ) {
      if (receiptsRelation.remove) {
        receiptsRelation.remove.push(originalReceipt.id);
      }
    }
  });

  return receiptsRelation;
};
